import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import { invalid } from '../validation.ts';
import {
  domError,
  errorText,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

interface NavigatorWithKeyboard extends Navigator {
  readonly keyboard?: {
    getLayoutMap(): Promise<Map<string, string>>;
  };
}

async function collect(): Promise<RawData> {
  const keyboard = (navigator as NavigatorWithKeyboard).keyboard;
  if (!keyboard?.getLayoutMap) {
    return { available: false };
  }
  try {
    const layout = await keyboard.getLayoutMap();
    const entries = [...layout.entries()].sort(([left], [right]) =>
      left.localeCompare(right),
    );
    return {
      entries,
    };
  } catch (error) {
    return {
      errorName: domError(error).name,
      errorMessage: domError(error).message,
    };
  }
}

function validateLayoutMap(rawData: RawData): ValidationResult {
  if (rawData.available == false) {
    return invalid('Keyboard layout map is unavailable');
  }
  if (rawData.errorName) {
    return invalid(
      `Keyboard layout map failed with ${String(rawData.errorName)}`,
    );
  }
  const entries = Array.isArray(rawData.entries) ? rawData.entries : [];
  return entries.length > 0
    ? validation([])
    : invalid('Keyboard layout map is empty or has an invalid count');
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  const entries = Array.isArray(rawData.entries) ? rawData.entries : [];
  return rawData.available == false
    ? 'Unavailable'
    : `${String(entries.length)} entries`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateLayoutMap(rawData);
}

export const keyboardLayoutMapTest: TestDefinition = {
  id: 'keyboardLayoutMap',
  title: 'Keyboard layout map',
  category: 'shared',
  format,
  run,
  validate,
};
